import React, { useState, useRef, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { XIcon, MailIcon, SmartphoneIcon, LinkIcon, CheckIcon, UserIcon, EyeOffIcon, SendIcon, DollarSignIcon, QrCodeIcon, DownloadIcon, CalendarIcon, PercentIcon, BuildingIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Recipient, Supplier, Installment } from './InstallmentPayment';
import { QRCodeSVG } from 'qrcode.react';
interface ShareLinkModalProps {
  onClose: () => void;
  splitType?: 'dates' | 'recipients' | 'commission' | null;
  recipients?: Recipient[];
  installments?: Installment[];
  suppliers?: Supplier[];
  totalAmount?: string;
  commissionType?: 'percentage' | 'fixed';
}
export function ShareLinkModal({
  onClose,
  splitType = null,
  recipients = [],
  installments = [],
  suppliers = [],
  totalAmount = '0',
  commissionType = 'percentage'
}: ShareLinkModalProps) {
  const {
    t
  } = useTranslation('payments');
  const [step, setStep] = useState<'preview' | 'share'>(() => {
    // Recipients mode goes straight to share (multi-recipient view)
    // No splitType (standard) also goes straight to share
    if (splitType === 'recipients' || !splitType) return 'share';
    // Schedule and commission start with preview
    return 'preview';
  });
  const [selectedOption, setSelectedOption] = useState<'email' | 'sms' | 'copy' | 'qrcode' | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [qrDownloaded, setQrDownloaded] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  // For multiple recipients
  const [recipientContactMethods, setRecipientContactMethods] = useState<Record<number, 'email' | 'sms' | 'both'>>({});
  const [copiedRecipientId, setCopiedRecipientId] = useState<number | null>(null);
  const [recipientsViewTotalSettings, setRecipientsViewTotalSettings] = useState<Record<number, boolean>>(() => {
    const initialSettings: Record<number, boolean> = {};
    recipients.forEach(r => {
      initialSettings[r.id] = false;
    });
    return initialSettings;
  });
  const hasMultipleRecipients = splitType === 'recipients' && recipients.length > 0;
  const handleCopyLink = (recipientId?: number) => {
    const linkToCopy = recipientId ? `https://payment.example.com/pay/${recipientId}-abc123${recipientsViewTotalSettings[recipientId] ? '?showTotal=true' : ''}` : 'https://payment.example.com/pay/abc123';
    navigator.clipboard.writeText(linkToCopy);
    if (recipientId) {
      setCopiedRecipientId(recipientId);
      setTimeout(() => setCopiedRecipientId(null), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  const handleDownloadQR = () => {
    if (!qrCodeRef.current) return;
    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'payment-qrcode.png';
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setQrDownloaded(true);
    setTimeout(() => setQrDownloaded(false), 2000);
  };
  const handleSend = () => {
    onClose();
  };
  const getContactMethod = (recipient: Recipient): 'email' | 'sms' | 'both' => {
    if (recipientContactMethods[recipient.id]) {
      return recipientContactMethods[recipient.id];
    }
    if (recipient.email && recipient.phone) return 'both';
    if (recipient.email) return 'email';
    if (recipient.phone) return 'sms';
    return 'email';
  };
  const setContactMethod = (recipientId: number, method: 'email' | 'sms' | 'both') => {
    setRecipientContactMethods(prev => ({
      ...prev,
      [recipientId]: method
    }));
  };
  const toggleRecipientTotalView = (recipientId: number) => {
    setRecipientsViewTotalSettings(prev => ({
      ...prev,
      [recipientId]: !prev[recipientId]
    }));
  };
  // Calculate commission values
  const totalAmountNum = parseFloat(totalAmount) || 0;
  const totalCommission = suppliers.reduce((sum, s) => {
    if (commissionType === 'percentage') {
      return sum + totalAmountNum * (parseFloat(s.percentage || '0') / 100);
    }
    return sum + (parseFloat(s.amount) || 0);
  }, 0);
  const businessShare = totalAmountNum - totalCommission;
  const getModalTitle = () => {
    if (step === 'preview') {
      if (splitType === 'dates') return 'Payment schedule preview';
      if (splitType === 'commission') return 'Commission split preview';
    }
    if (hasMultipleRecipients) return 'Share payment link';
    return 'Share payment link';
  };
  // ─── SCHEDULE PREVIEW ───
  const renderSchedulePreview = () => <div className="space-y-4" data-id="element-8767" data-mp="2j90awkgahjoj">
      <p className="text-sm text-gray-600" data-id="element-8768" data-mp="2j9s7nylmhnge">
        {t('components.renderSchedulePreview.review_the_payment_schedule_before_creating')}
      </p>

      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-8769" data-mp="13pj76ptn5upn">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-8770" data-mp="13rnab5jpupov">
          <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8771" data-mp="p0dab8ia0gkx">
            <div className="col-span-1" data-id="element-8772" data-mp="ty6czrkdesgg">#</div>
            <div className="col-span-3" data-id="element-8773" data-mp="fzovwcoz0e7i">
              {t('components.BulkPaymentPreview.amount')}
            </div>
            <div className="col-span-3" data-id="element-8774" data-mp="l0ck6qltegy1">
              {t('components.InstallmentPayment.due_date')}
            </div>
            <div className="col-span-3" data-id="element-8775" data-mp="63ll6ig1z78y">
              {t('components.InstallmentPayment.send_link_on')}
            </div>
            <div className="col-span-2" data-id="element-8776" data-mp="b37zywbj4vjv">
              {t('components.BulkPaymentList.status')}
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-8777" data-mp="25fekwmu86qpd">
          {installments.map((inst, index) => <div key={inst.id} className="px-4 py-3.5" data-id="element-8778" data-mp="23y96na2aoy7r" data-mpi={index}>
              <div className="grid grid-cols-12 gap-3 items-center" data-id="element-8779" data-mp="23vmo6fdv8yar">
                <div className="col-span-1" data-id="element-8780" data-mp="1yxbeq5rhgpt2">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#E2231A]/10 text-[#9F140F] rounded-full font-bold text-xs" data-id="element-8781" data-mp="1tztb8uw8j2sg">
                    {index + 1}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-8782" data-mp="2mp0bbybc80ft">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-8783" data-mp="2mnp8c0ppuwpr">
                    {formatCurrencyAmount(parseFloat(inst.amount || '0'), {
                  fractionDigits: 2
                })}
                  </span>
                  {inst.description && <p className="text-xs text-gray-500 mt-0.5" data-id="element-8784" data-mp="28waq3w3muzjr">
                      {inst.description}
                    </p>}
                </div>
                <div className="col-span-3" data-id="element-8785" data-mp="30nq8tf22k24p">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-8786" data-mp="30myqh5xhg0wd">
                    <CalendarIcon size={13} className="mr-1.5 text-gray-400" data-id="element-8787" data-mp="n6x8yeaxzsfr" />
                    {inst.dueDate || 'Not set'}
                  </div>
                </div>
                <div className="col-span-3" data-id="element-8788" data-mp="w5sedo65wyi0">
                  <div className="flex items-center text-sm text-gray-700" data-id="element-8789" data-mp="w4z4wfftjuw6">
                    <SendIcon size={13} className="mr-1.5 text-gray-400" data-id="element-8790" data-mp="116g7qghv5rfe" />
                    {inst.sendLinkDate || 'Auto'}
                  </div>
                </div>
                <div className="col-span-2" data-id="element-8791" data-mp="1b3azmgjmstkp">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-8792" data-mp="1b1zzqrtb45yv">
                    {t('components.renderSchedulePreview.pending')}
                  </span>
                </div>
              </div>
            </div>)}
        </div>
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-8793" data-mp="1tu27fdnz0bwz">
          <div className="flex justify-between items-center" data-id="element-8794" data-mp="1tubxf190mj2o">
            <span className="text-sm font-medium text-gray-600" data-id="element-8795" data-mp="14q3uhy9kaiws">
              {t('components.renderSchedulePreview.total')}
            </span>
            <span className="text-sm font-bold text-gray-900" data-id="element-8796" data-mp="ztwqib0q7hhh">
              {formatCurrencyAmount(installments.reduce((sum, i) => sum + parseFloat(i.amount || '0'), 0), {
              fractionDigits: 2
            })}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[#E2231A]/5 border border-[#E2231A]/20 rounded-xl p-4" data-id="element-8797" data-mp="h35jhg47o369">
        <div className="flex items-start gap-3" data-id="element-8798" data-mp="utip3btmr5sf">
          <CalendarIcon size={18} className="text-[#9F140F] mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-8799" data-mp="q2944oj10qmv" />

          <div data-id="element-8800" data-mp="2avb0gd1wr0u">
            <p className="text-sm font-medium text-gray-900" data-id="element-8801" data-mp="2alh4hovxzpv">
              {t('components.renderSchedulePreview.automatic_link_delivery')}
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-id="element-8802" data-mp="3kc0jc09a2yeb">
              {t('components.renderSchedulePreview.payment_links_will_be_sent_automatically')}
            </p>
          </div>
        </div>
      </div>
    </div>;
  // ─── COMMISSION PREVIEW ───
  const renderCommissionPreview = () => <div className="space-y-4" data-id="element-8803" data-mp="3fdpa9rqt4rx5">
      <p className="text-sm text-gray-600" data-id="element-8804" data-mp="3fauuidiqnva2">
        {t('components.renderCommissionPreview.review_the_commission_breakdown_before_creating')}
      </p>

      {/* Total amount card */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5" data-id="element-8805" data-mp="3t4m1be535123">
        <div className="flex items-center justify-between mb-4" data-id="element-8806" data-mp="3t6oq5uui4o58">
          <span className="text-sm font-medium text-gray-500" data-id="element-8807" data-mp="1x9lp3juovoa">
            {t('components.InstallmentPayment.total_payment_amount')}
          </span>
          <span className="text-xl font-bold text-gray-900" data-id="element-8808" data-mp="2hrrwg7ndharj">
            {formatCurrencyAmount(totalAmountNum, {
            fractionDigits: 2
          })}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-8809" data-mp="y73wgxwqvs34">
          <div className="h-full rounded-full bg-gradient-to-r from-[#E2231A] to-[#E2231A]/70" style={{
          width: totalAmountNum > 0 ? `${Math.min(totalCommission / totalAmountNum * 100, 100)}%` : '0%'
        }} data-id="element-8810" data-mp="133b6p2w6bpqv" />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500" data-id="element-8811" data-mp="fgmi5ycnsz1a">
          <span className="flex items-center gap-1" data-id="element-8812" data-mp="keo18ja34ycr">
            <span className="w-2 h-2 rounded-full bg-[#E2231A]" data-id="element-8813" data-mp="5glz8dipckxm" />
            {t('components.renderCommissionPreview.commission_usd')}{' '}
            {formatCurrencyAmount(totalCommission, {
            fractionDigits: 2
          })}
          </span>
          <span className="flex items-center gap-1" data-id="element-8814" data-mp="1zs0l2j8le083">
            <span className="w-2 h-2 rounded-full bg-gray-300" data-id="element-8815" data-mp="24s54oi4srb2j" />
            {t('components.renderCommissionPreview.your_share_usd')}{' '}
            {formatCurrencyAmount(businessShare, {
            fractionDigits: 2
          })}
          </span>
        </div>
      </div>

      {/* Supplier breakdown */}
      <div className="border border-gray-200 rounded-xl overflow-hidden" data-id="element-8816" data-mp="2wfs5wyskecu">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-8817" data-mp="3ua20ahyaqdbz">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8818" data-mp="bwca7fqoeeyx">
            {t('components.renderCommissionPreview.supplier_commission_breakdown')}
          </h4>
        </div>
        <div className="divide-y divide-gray-100" data-id="element-8819" data-mp="lus4tsoww6d6">
          {suppliers.map((supplier, index) => {
          const supplierAmount = commissionType === 'percentage' ? totalAmountNum * (parseFloat(supplier.percentage || '0') / 100) : parseFloat(supplier.amount) || 0;
          const supplierPercentage = commissionType === 'percentage' ? parseFloat(supplier.percentage || '0') : totalAmountNum > 0 ? supplierAmount / totalAmountNum * 100 : 0;
          return <div key={supplier.id} className="px-4 py-4" data-id="element-8820" data-mp="vjkd3z0vepi6" data-mpi={index}>
                <div className="flex items-center justify-between" data-id="element-8821" data-mp="10j6rwcwckdt3">
                  <div className="flex items-center gap-3" data-id="element-8822" data-mp="10hc4mrxls9lu">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#E2231A]/10 text-[#9F140F] rounded-full font-bold text-xs flex-shrink-0" data-id="element-8823" data-mp="15hgomrxpzmgt">
                      {index + 1}
                    </div>
                    <div data-id="element-8824" data-mp="1f8ut7e4j3cgt">
                      <p className="text-sm font-semibold text-gray-900" data-id="element-8825" data-mp="1fap1nsym5c40">
                        {supplier.name || `Supplier ${index + 1}`}
                      </p>
                      {supplier.email && <p className="text-xs text-gray-500" data-id="element-8826" data-mp="1t83xbffgmg4f">
                          {supplier.email}
                        </p>}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-8827" data-mp="1ycy0q1kxql0h">
                    <p className="text-sm font-bold text-gray-900" data-id="element-8828" data-mp="1yc69t42a9bgj">
                      {formatCurrencyAmount(supplierAmount, {
                    fractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-[#9F140F] font-medium" data-id="element-8829" data-mp="1pecjc9r79c1a">
                      {supplierPercentage.toFixed(1)}
                      {t('components.renderCommissionPreview.of_total')}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Your business share */}
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-200" data-id="element-8830" data-mp="28a2mw1es6gha">
          <div className="flex items-center justify-between" data-id="element-8831" data-mp="34srw0n6laqf6">
            <div className="flex items-center gap-3" data-id="element-8832" data-mp="34v4ldleog329">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full flex-shrink-0" data-id="element-8833" data-mp="3021mqyv1m3f7">
                <BuildingIcon size={14} data-id="element-8834" data-mp="2kipb3cqtxdvw" />
              </div>
              <div data-id="element-8835" data-mp="2pjwk1f4pkj45">
                <p className="text-sm font-semibold text-gray-900" data-id="element-8836" data-mp="2pkeq13h6abp9">
                  {t('components.renderCommissionPreview.your_business')}
                </p>
                <p className="text-xs text-gray-500" data-id="element-8837" data-mp="2fkxj6iq6prrp">
                  {t('components.renderCommissionPreview.remaining_after_commission')}
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-8838" data-mp="388jl7mjrkocg">
              <p className="text-sm font-bold text-gray-900" data-id="element-8839" data-mp="38bpc97bye0at">
                {formatCurrencyAmount(businessShare, {
                fractionDigits: 2
              })}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-8840" data-mp="2yhyoy5rkbvwp">
                {totalAmountNum > 0 ? (businessShare / totalAmountNum * 100).toFixed(1) : '0.0'}
                {t('components.renderCommissionPreview.of_total')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  // ─── MULTI-RECIPIENT SHARE VIEW ───
  const renderMultiRecipientShare = () => <>
      <div className="flex justify-between items-center mb-4" data-id="element-8841" data-mp="1pbga6hd6tkr7">
        <p className="text-gray-600 font-medium" data-id="element-8842" data-mp="1pd18mi2mhnuc">
          {t('components.renderMultiRecipientShare.send_unique_payment_links_to_each')}
        </p>
      </div>
      <div className="space-y-4 max-h-[50vh] overflow-y-auto" data-id="element-8843" data-mp="woltlvjhr5j0">
        {recipients.map((recipient, index) => <div key={recipient.id} className="border border-gray-200 rounded-xl p-4" data-id="element-8844" data-mp="16kp5ht45aij5" data-mpi={index}>
            <div className="flex items-center mb-3" data-id="element-8845" data-mp="11l2t1lw556be">
              <div className="w-10 h-10 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-8846" data-mp="11kavw6mwb0j0">
                <UserIcon size={20} className="text-[#9F140F]" aria-hidden="true" data-id="element-8847" data-mp="2i1oxenmkyvcn" />
              </div>
              <div className="ml-3" data-id="element-8848" data-mp="2k3pkd6sp3a7s">
                <h4 className="font-medium text-gray-800" data-id="element-8849" data-mp="2p2sevxb1ty1i">
                  {t('components.renderMultiRecipientShare.recipient')}{' '}
                  {index + 1}
                </h4>
                <p className="text-sm text-gray-500" data-id="element-8850" data-mp="2u0anfml5mb8z">
                  {recipient.email || recipient.phone}
                </p>
              </div>
            </div>

            {/* Toggle */}
            <div className="mb-3" data-id="element-8851" data-mp="21bmz1i53z5r7">
              <label className="flex items-center cursor-pointer" data-id="element-8852" data-mp="26c16axyvybo6">
                <input type="checkbox" className="sr-only" checked={recipientsViewTotalSettings[recipient.id] || false} onChange={() => toggleRecipientTotalView(recipient.id)} data-id="element-8853" data-mp="26a84bvz7wfmi" />

                <div className={`w-10 h-5 rounded-full transition-colors ${recipientsViewTotalSettings[recipient.id] ? 'bg-[#E2231A]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-8854" data-mp="1imgncf2qfeew">
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${recipientsViewTotalSettings[recipient.id] ? 'translate-x-5' : ''}`} data-id="element-8855" data-mp="4ouigmp40g0a" />
                </div>
                <span className="ml-2 text-sm text-gray-600" data-id="element-8856" data-mp="3mwjvw99sjbjz">
                  {recipientsViewTotalSettings[recipient.id] ? <span className="flex items-center" data-id="element-8857" data-mp="3hyigbybkpud9">
                      <DollarSignIcon size={14} className="mr-1" data-id="element-8858" data-mp="3hzbbs3530xg3" />
                      {t('components.renderMultiRecipientShare.allow_recipient_to_view_the_total')}
                    </span> : <span className="flex items-center" data-id="element-8859" data-mp="347n4cd5s2x5g">
                      <EyeOffIcon size={14} className="mr-1" data-id="element-8860" data-mp="2z7ij69txde8w" />
                      {t('components.renderMultiRecipientShare.recipient_will_only_see_their_portion')}
                    </span>}
                </span>
              </label>
            </div>

            {/* Payment link */}
            <div className="mb-3" data-id="element-8861" data-mp="qw6ppu4sj3wq">
              <div className="flex" data-id="element-8862" data-mp="cyrta5g4dvv9">
                <input type="text" className="flex-1 px-3 py-2 border border-gray-200 rounded-l-lg focus:ring-#E2231A/20 focus:border-#E2231A bg-gray-50 text-sm" value={`https://payment.example.com/pay/${recipient.id}-abc123${recipientsViewTotalSettings[recipient.id] ? '?showTotal=true' : ''}`} readOnly data-id="element-8863" data-mp="cxq8h99axv0c" />

                <button className="px-3 py-2 bg-[#E2231A] text-white rounded-r-lg hover:bg-[#EB4A42] transition-colors flex items-center text-sm font-medium" onClick={() => handleCopyLink(recipient.id)} data-id="element-8864" data-mp="3qephplxamtqx">
                  {copiedRecipientId === recipient.id ? <>
                      <CheckIcon size={14} className="mr-1" data-id="element-8865" data-mp="3lg5yvudzxy2s" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={14} className="mr-1" data-id="element-8866" data-mp="3bqpjhyogdz0a" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>

            {/* Contact options */}
            {(recipient.email || recipient.phone) && <div className="mt-3" data-id="element-8867" data-mp="3gqlnhxx5hqfu">
                <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-8868" data-mp="3gpjzksv1d9gp">
                  {t('components.renderMultiRecipientShare.send_payment_link_via')}
                </h5>
                <div className="flex space-x-2" data-id="element-8869" data-mp="3pkggk7kzzqf4">
                  {recipient.email && recipient.phone && <>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'email' ? 'bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'email')} data-id="element-8870" data-mp="3bfheqkkctsi">
                        <MailIcon size={14} className="mr-1.5" data-id="element-8871" data-mp="kti57ik0pbe8" />
                        {t('components.RecurringPayment.email')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'sms' ? 'bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'sms')} data-id="element-8872" data-mp="3oa8lcq9bd4tn">
                        <SmartphoneIcon size={14} className="mr-1.5" data-id="element-8873" data-mp="35gv7jxxqff19" />
                        {t('components.renderMultiRecipientShare.sms')}
                      </button>
                      <button className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${getContactMethod(recipient) === 'both' ? 'bg-[#E2231A] text-white border border-[#E2231A]' : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'}`} onClick={() => setContactMethod(recipient.id, 'both')} data-id="element-8874" data-mp="306izfq1ce9zl">
                        {t('components.renderMultiRecipientShare.both')}
                      </button>
                    </>}
                  {recipient.email && !recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30 cursor-default" data-id="element-8875" data-mp="1ss5t4eoziluu">
                      <MailIcon size={14} className="mr-1.5" data-id="element-8876" data-mp="1xowar6gftpid" />
                      {t('components.renderMultiRecipientShare.email_only')}
                    </button>}
                  {!recipient.email && recipient.phone && <button className="flex-1 py-2 px-3 rounded-lg flex items-center justify-center text-sm font-medium bg-[#E2231A]/10 text-[#E2231A] border border-[#E2231A]/30 cursor-default" data-id="element-8877" data-mp="3qn2xk9xutjpu">
                      <SmartphoneIcon size={14} className="mr-1.5" data-id="element-8878" data-mp="3gqztgyjfzb09" />
                      {t('components.renderMultiRecipientShare.sms_only')}
                    </button>}
                </div>
              </div>}

            {/* Display contact info */}
            <div className="mt-3 text-sm space-y-1" data-id="element-8879" data-mp="32qyu5nitsbmw">
              {(getContactMethod(recipient) === 'email' || getContactMethod(recipient) === 'both') && recipient.email && <div className="flex items-center text-gray-600" data-id="element-8880" data-mp="10zu6t3nxdx8d">
                    <MailIcon size={14} className="mr-1.5" data-id="element-8881" data-mp="18il7h0sl1p8m" />
                    {recipient.email}
                  </div>}
              {(getContactMethod(recipient) === 'sms' || getContactMethod(recipient) === 'both') && recipient.phone && <div className="flex items-center text-gray-600" data-id="element-8882" data-mp="1n6y84b2m7w9g">
                    <SmartphoneIcon size={14} className="mr-1.5" data-id="element-8883" data-mp="1n9ancggznsit" />
                    {recipient.phone}
                  </div>}
            </div>
          </div>)}
      </div>
    </>;
  // ─── STANDARD SHARE VIEW (single link) ───
  const renderStandardShare = () => <>
      <p className="text-gray-600 mb-4" data-id="element-8884" data-mp="zjq3lvd0gr8h">
        {t('components.renderStandardShare.choose_how_you_want_to_share')}
      </p>
      <div className="space-y-3" data-id="element-8885" data-mp="2awsgogfwiynw">
        {/* Email option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'email' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('email')} data-id="element-8886" data-mp="2awismyef31ku">
          <div className="flex items-center" data-id="element-8887" data-mp="29h7o03sb5es0">
            <div className="w-10 h-10 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-8888" data-mp="2nf4np92261aw">
              <MailIcon size={20} className="text-[#9F140F]" data-id="element-8889" data-mp="2ifrywirmmk5o" />
            </div>
            <div className="ml-4 flex-1" data-id="element-8890" data-mp="2wg2qjo0rg2rw">
              <h4 className="font-medium text-gray-800" data-id="element-8891" data-mp="2werq9y6ixd5j">
                {t('components.renderStandardShare.send_via_email')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8892" data-mp="2sco9qv4zzide">
                {t('components.renderStandardShare.send_the_payment_link_to_their')}
              </p>
            </div>
          </div>
          {selectedOption === 'email' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8893" data-mp="3g4bka2hycgti">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8894" data-mp="3b61q9v7yrm9h">
                {t('components.InstallmentPayment.attrs.email_address')}
              </label>
              <input type="email" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#E2231A/20 focus:border-#E2231A transition-colors" placeholder={t('components.renderStandardShare.attrs.nameexamplecom')} value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-8895" data-mp="2mq05t3rohas" />
            </div>}
        </div>

        {/* SMS option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'sms' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('sms')} data-id="element-8896" data-mp="32365naubsw50">
          <div className="flex items-center" data-id="element-8897" data-mp="2x39xczeliijn">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-8898" data-mp="2x54jui5imipu">
              <SmartphoneIcon size={20} className="text-green-600" data-id="element-8899" data-mp="2e086qx1vyqpm" />
            </div>
            <div className="ml-4 flex-1" data-id="element-8900" data-mp="295lqogkv09z7">
              <h4 className="font-medium text-gray-800" data-id="element-8901" data-mp="2482edm7q279w">
                {t('components.renderStandardShare.send_via_text_message')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8902" data-mp="1z7ecg5hyg60w">
                {t('components.renderStandardShare.send_the_payment_link_to_their_2')}
              </p>
            </div>
          </div>
          {selectedOption === 'sms' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8903" data-mp="2mxs59y8d68wj">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8904" data-mp="2i0ias464b71u">
                {t('components.InstallmentPayment.attrs.phone_number')}
              </label>
              <input type="tel" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-#E2231A/20 focus:border-#E2231A transition-colors" placeholder="+1 (555) 123-4567" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} data-id="element-8905" data-mp="1bhk3w04o53mg" />
            </div>}
        </div>

        {/* QR Code option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'qrcode' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('qrcode')} data-id="element-8906" data-mp="sgvk7wonls8">
          <div className="flex items-center" data-id="element-8907" data-mp="eshzjw88v9bx">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-8908" data-mp="epndk09l1gge">
              <QrCodeIcon size={20} className="text-amber-600" data-id="element-8909" data-mp="2gitfre09ww7s" />
            </div>
            <div className="ml-4 flex-1" data-id="element-8910" data-mp="2gl63y8wmk2ta">
              <h4 className="font-medium text-gray-800" data-id="element-8911" data-mp="2lh4ofleu10of">
                {t('components.renderStandardShare.generate_qr_code')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8912" data-mp="r7zhgk0g02z6">
                {t('components.renderStandardShare.create_a_scannable_qr_code_for')}
              </p>
            </div>
          </div>
          {selectedOption === 'qrcode' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8913" data-mp="w1areu0jwzvb">
              <div className="flex flex-col items-center" data-id="element-8914" data-mp="8aolp9e0rroh">
                <div ref={qrCodeRef} className="bg-white p-4 rounded-lg mb-3" data-id="element-8915" data-mp="8das35p6c154">
                  <QRCodeSVG value="https://payment.example.com/pay/abc123" size={200} level="H" includeMargin={true} data-id="element-8916" data-mp="3dy3oginmm0f" />
                </div>
                <button className="px-4 py-2 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors flex items-center font-medium text-sm" onClick={handleDownloadQR} data-id="element-8917" data-mp="1toat0d60i3cm">
                  {qrDownloaded ? <>
                      <CheckIcon size={16} className="mr-2" data-id="element-8918" data-mp="217sh83u7bhfi" />
                      {t('components.renderStandardShare.downloaded')}
                    </> : <>
                      <DownloadIcon size={16} className="mr-2" data-id="element-8919" data-mp="1r9mdrhsqy38j" />
                      {t('components.renderStandardShare.download_qr_code')}
                    </>}
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-8920" data-mp="2jz32o5h5r200">
                  {t('components.renderStandardShare.customers_can_scan_this_qr_code')}
                </p>
              </div>
            </div>}
        </div>

        {/* Copy link option */}
        <div className={`border rounded-xl p-4 cursor-pointer transition-all ${selectedOption === 'copy' ? 'border-[#E2231A] bg-[#E2231A]/[0.03] ring-1 ring-[#E2231A]' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedOption('copy')} data-id="element-8921" data-mp="2xx9v363c89sa">
          <div className="flex items-center" data-id="element-8922" data-mp="c1qsncbpsjz4">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-8923" data-mp="c0ftxqx4m2ev">
              <LinkIcon size={20} className="text-purple-600" data-id="element-8924" data-mp="gvbk0lisyfpc" />
            </div>
            <div className="ml-4 flex-1" data-id="element-8925" data-mp="lraemquq4ty7">
              <h4 className="font-medium text-gray-800" data-id="element-8926" data-mp="3l7gtciseyxlo">
                {t('components.renderStandardShare.copy_link_to_share')}
              </h4>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-8927" data-mp="3p9aokbjub9eg">
                {t('components.renderStandardShare.copy_the_payment_link_to_share')}
              </p>
            </div>
          </div>
          {selectedOption === 'copy' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-8928" data-mp="19gl8rphkjq3d">
              <div className="flex" data-id="element-8929" data-mp="1eghcdnmctfie">
                <input type="text" className="flex-1 px-3 py-2.5 border border-gray-200 rounded-l-lg focus:ring-#E2231A/20 focus:border-#E2231A bg-gray-50 text-sm" value="https://payment.example.com/pay/abc123" readOnly data-id="element-8930" data-mp="1efoap1295g74" />

                <button className="px-4 py-2.5 bg-[#E2231A] text-white rounded-r-lg hover:bg-[#EB4A42] transition-colors flex items-center font-medium text-sm" onClick={() => handleCopyLink()} data-id="element-8931" data-mp="2p7e0plquetxa">
                  {copied ? <>
                      <CheckIcon size={16} className="mr-1" data-id="element-8932" data-mp="2b7mif8tamrdh" />
                      {t('components.renderMultiRecipientShare.copied')}
                    </> : <>
                      <LinkIcon size={16} className="mr-1" data-id="element-8933" data-mp="21cugxtwlbkdf" />
                      {t('components.renderMultiRecipientShare.copy')}
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </>;
  // ─── FOOTER BUTTONS ───
  const renderFooter = () => {
    if (step === 'preview') {
      return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-8934" data-mp="1ys69q5mwcffg">
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-8935" data-mp="1k2gwvd5fymzc">
            {t('components.CardOnFilePayment.cancel')}
          </button>
          <button className="px-5 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors flex items-center text-sm font-medium" onClick={() => setStep('share')} data-id="element-8936" data-mp="2hp7i80fnsxo5">
            {t('components.renderFooter.continue_to_share')}
            <ArrowRightIcon size={16} className="ml-2" data-id="element-8937" data-mp="27t4hmz0gh73b" />
          </button>
        </div>;
    }
    // Share step
    const showBackButton = splitType === 'dates' || splitType === 'commission';
    return <div className="p-4 border-t border-gray-200 flex justify-between" data-id="element-8938" data-mp="oslrir07gugn">
        <div className="flex items-center gap-2" data-id="element-8939" data-mp="ouomzqk4nfsr">
          {showBackButton && <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center" onClick={() => setStep('preview')} data-id="element-8940" data-mp="jszeoioahg8d">
              <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-8941" data-mp="61uixro1l83b" />
              {t('components.BulkPaymentPreview.back')}
            </button>}
          <button className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium" onClick={onClose} data-id="element-8942" data-mp="14klppuevs4x">
            {t('components.CardOnFilePayment.cancel')}
          </button>
        </div>
        <div data-id="element-8943" data-mp="3jfz0psulrlqz">
          {hasMultipleRecipients ? <button className="px-5 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors flex items-center text-sm font-medium" onClick={handleSend} data-id="element-8944" data-mp="3ehxgh4lfxgdx">
              <SendIcon size={16} className="mr-2" data-id="element-8945" data-mp="2lrm8semaahwb" />
              {t('components.renderFooter.send_to_all_recipients')}
            </button> : <>
              {(selectedOption === 'email' || selectedOption === 'sms') && <button className="px-5 py-2.5 bg-#E2231A text-white rounded-lg hover:bg-#EB4A42 transition-colors flex items-center text-sm font-medium disabled:opacity-50" onClick={handleSend} disabled={!recipientInput} data-id="element-8946" data-mp="2vj8ho4b5g4vw">
                  <SendIcon size={16} className="mr-2" data-id="element-8947" data-mp="39ihwazjpiqxr" />
                  {t('components.renderFooter.send')}
                </button>}
              {(selectedOption === 'copy' || selectedOption === 'qrcode') && <button className="px-5 py-2.5 bg-[#E2231A] text-white rounded-lg hover:bg-[#EB4A42] transition-colors text-sm font-medium" onClick={onClose} data-id="element-8948" data-mp="1k2saszdnuwkn">
                  {t('components.renderFooter.done')}
                </button>}
              {!selectedOption && <button className="px-5 py-2.5 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-not-allowed" disabled data-id="element-8949" data-mp="12uhhj4vhnwcc">
                  {t('components.renderFooter.select_an_option')}
                </button>}
            </>}
        </div>
      </div>;
  };
  return createPortal(<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-id="element-8950" data-mp="1ljeexhm6n4ys">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden" data-id="element-8951" data-mp="1him4il1kqu0x">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-8952" data-mp="a50wisa77hq5">
          <div className="flex items-center gap-3" data-id="element-8953" data-mp="62no1w42h5po">
            {step === 'preview' && splitType === 'dates' && <div className="w-8 h-8 rounded-lg bg-[#E2231A]/10 flex items-center justify-center" data-id="element-8954" data-mp="7le0d8hisz9w">
                <CalendarIcon size={16} className="text-[#9F140F]" data-id="element-8955" data-mp="cie2x9f5vjw9" />
              </div>}
            {step === 'preview' && splitType === 'commission' && <div className="w-8 h-8 rounded-lg bg-[#E2231A]/10 flex items-center justify-center" data-id="element-8956" data-mp="2kzt3snqttcx">
                <PercentIcon size={16} className="text-[#9F140F]" data-id="element-8957" data-mp="3kypg2m02hblr" />
              </div>}
            <div data-id="element-8958" data-mp="3pqh9d4alxvfl">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-8959" data-mp="3pr0qwjwciht3">
                {getModalTitle()}
              </h3>
              {step === 'preview' && <div className="flex items-center gap-2 mt-1" data-id="element-8960" data-mp="3g05tqkhk9xsv">
                  <div className="flex items-center gap-1" data-id="element-8961" data-mp="3g0o049xxtsei">
                    <div className="w-5 h-5 rounded-full bg-[#E2231A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-8962" data-mp="3204rb0lneiba">
                      1
                    </div>
                    <span className="text-xs font-medium text-[#9F140F]" data-id="element-8963" data-mp="2nd62lde6e209">
                      {t('components.ShareLinkModal.review')}
                    </span>
                  </div>
                  <div className="w-4 h-px bg-gray-300" data-id="element-8964" data-mp="2wecmzxjy3wys" />
                  <div className="flex items-center gap-1" data-id="element-8965" data-mp="2wfxgrkyfrbvl">
                    <div className="w-5 h-5 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-[10px] font-bold" data-id="element-8966" data-mp="2rerk5c1nn4gs">
                      2
                    </div>
                    <span className="text-xs text-gray-400" data-id="element-8967" data-mp="31dpb0lmjza2y">
                      {t('components.ShareLinkModal.share')}
                    </span>
                  </div>
                </div>}
              {step === 'share' && (splitType === 'dates' || splitType === 'commission') && <div className="flex items-center gap-2 mt-1" data-id="element-8968" data-mp="3p433uecypcyl">
                    <div className="flex items-center gap-1" data-id="element-8969" data-mp="3k3yhw8tabq0z">
                      <div className="w-5 h-5 rounded-full bg-[#E2231A] text-white flex items-center justify-center" data-id="element-8970" data-mp="3k5rtm2ir90fu">
                        <CheckIcon size={10} data-id="element-8971" data-mp="vdclbs3hjp6k" />
                      </div>
                      <span className="text-xs text-gray-400" data-id="element-8972" data-mp="zb0nng7ick8h">
                        {t('components.ShareLinkModal.review')}
                      </span>
                    </div>
                    <div className="w-4 h-px bg-[#E2231A]" data-id="element-8973" data-mp="199gctdmz5jfc" />
                    <div className="flex items-center gap-1" data-id="element-8974" data-mp="bnam41nf2wyj">
                      <div className="w-5 h-5 rounded-full bg-[#E2231A] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-8975" data-mp="bou35ugwdc1d">
                        2
                      </div>
                      <span className="text-xs font-medium text-[#9F140F]" data-id="element-8976" data-mp="ljvsaqb6ae3y">
                        {t('components.ShareLinkModal.share')}
                      </span>
                    </div>
                  </div>}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-8977" data-mp="3u08f1olskpj7">
            <XIcon size={20} aria-hidden="true" data-id="element-8978" data-mp="3hiefypvntpjy" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[65vh] overflow-y-auto" data-id="element-8979" data-mp="3cjbe1eaz7zg7">
          {step === 'preview' && splitType === 'dates' && renderSchedulePreview()}
          {step === 'preview' && splitType === 'commission' && renderCommissionPreview()}
          {step === 'share' && hasMultipleRecipients && renderMultiRecipientShare()}
          {step === 'share' && !hasMultipleRecipients && renderStandardShare()}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>, document.body);
}